// Minimal admin UI for viewing and updating inbound AI instructions.
// Tenant-scoped, no frameworks, no new dependencies.
// Supports cross-origin via apiBase; auth via Bearer token (matches app.js).

/**
 * Mount the Inbound AI Instructions admin UI.
 *
 * Expected usage from app.js:
 *   mountInboundInstructionsUI({ tenantId, container, apiBase, getAuthToken });
 *   mountInboundInstructionsUI({ tenantId, container, apiBase, getAuthToken, useAdminApi: true });
 *
 * @param {Object} options
 * @param {string} options.tenantId - Tenant identifier (required for API calls).
 * @param {HTMLElement} [options.container] - Optional DOM node to mount into.
 * @param {string} [options.apiBase] - API base URL (default: window.API_BASE or https://api.nightcoders.com).
 * @param {function} [options.getAuthToken] - Returns auth token (default: () => localStorage.authToken).
 * @param {string} [options.profileId] - Profile ID for instructions (default: 'default').
 * @param {boolean} [options.useAdminApi] - If true, fetch profiles from admin API (for super_admin editing another tenant).
 * @returns {HTMLElement|undefined} The root element of the mounted UI.
 */
function mountInboundInstructionsUI({
  tenantId,
  container,
  apiBase,
  getAuthToken,
  profileId: initialProfileId = 'default',
  useAdminApi = false
} = {}) {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.API_BASE) ||
    'https://api.nightcoders.com';
  const getToken =
    getAuthToken ||
    (typeof window !== 'undefined'
      ? () => window.localStorage && window.localStorage.getItem('authToken')
      : () => null);

  let root = container;
  if (!root) {
    root =
      document.getElementById('inbound-instructions-root') ||
      document.getElementById('admin-content') ||
      document.getElementById('admin-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'inbound-instructions-root';
      document.body.appendChild(root);
    }
  }

  root.innerHTML = '';

  const panel = document.createElement('div');
  panel.className = 'admin-panel inbound-instructions-panel';

  const title = document.createElement('h3');
  title.textContent = 'Instructions editor';
  title.className = 'section-heading';
  title.style.marginBottom = '8px';

  const profileRow = document.createElement('div');
  profileRow.className = 'field-row';
  profileRow.style.marginBottom = '12px';

  const profileLabel = document.createElement('label');
  profileLabel.textContent = 'Profile:';
  profileLabel.className = 'field';
  profileLabel.style.flex = '0 0 auto';

  const profileSelect = document.createElement('select');
  profileSelect.className = 'select';
  profileSelect.id = 'inbound-profile-select';
  profileSelect.setAttribute('aria-label', 'Inbound profile');
  profileLabel.htmlFor = 'inbound-profile-select';
  profileSelect.style.minWidth = '140px';
  profileSelect.innerHTML = '<option value="default">Loading...</option>';

  profileRow.appendChild(profileLabel);
  profileRow.appendChild(profileSelect);

  function populateProfiles(profilesList, selectedId) {
    profileSelect.innerHTML = '';
    const list = Array.isArray(profilesList) && profilesList.length ? profilesList : [{ profile_id: 'default', display_name: 'Default' }];
    list.forEach(function (p) {
      const pid = p.profile_id || p;
      const label = p.display_name || pid;
      const opt = document.createElement('option');
      opt.value = pid;
      opt.textContent = label;
      profileSelect.appendChild(opt);
    });
    const ids = list.map(function (p) { return (p.profile_id || p); });
    profileSelect.value = ids.indexOf(selectedId) >= 0 ? selectedId : (ids[0] || 'default');
  }

  const textarea = document.createElement('textarea');
  textarea.rows = 10;
  textarea.setAttribute('aria-label', 'Inbound instructions');
  textarea.className = 'input';
  textarea.style.fontFamily = 'monospace';
  textarea.style.fontSize = '0.85rem';
  textarea.style.minHeight = '180px';
  textarea.style.marginTop = '8px';
  textarea.style.width = '100%';
  textarea.style.boxSizing = 'border-box';

  const controls = document.createElement('div');
  controls.style.marginTop = '12px';
  controls.style.display = 'flex';
  controls.style.alignItems = 'center';
  controls.style.gap = '8px';

  const saveButton = document.createElement('button');
  saveButton.type = 'button';
  saveButton.textContent = 'Save';
  saveButton.className = 'button primary small';

  const statusText = document.createElement('div');
  statusText.className = 'field-help inbound-instructions-status';
  statusText.setAttribute('role', 'status');
  statusText.setAttribute('aria-live', 'polite');
  statusText.style.marginTop = '8px';
  statusText.style.minHeight = '1.2em';

  controls.appendChild(saveButton);
  panel.appendChild(profileRow);
  panel.appendChild(textarea);
  panel.appendChild(controls);
  panel.appendChild(statusText);
  root.appendChild(panel);

  function setStatus(message, type) {
    statusText.textContent = message || '';
    if (type === 'error') statusText.style.color = 'var(--danger)';
    else if (type === 'success') statusText.style.color = 'var(--success)';
    else if (type === 'info') statusText.style.color = 'var(--fg-muted)';
    else statusText.style.color = '';
  }

  function setSaving(isSaving) {
    saveButton.disabled = !!isSaving;
    saveButton.textContent = isSaving ? 'Saving...' : 'Save';
    saveButton.classList.toggle('disabled', !!isSaving);
  }

  function getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const token = typeof getToken === 'function' ? getToken() : null;
    if (token) headers['Authorization'] = 'Bearer ' + token;
    return headers;
  }

  function getProfileId() {
    const v = profileSelect.value;
    return v && String(v).trim() ? String(v) : 'default';
  }

  async function loadProfiles() {
    if (!tenantId) return;
    var profilesUrl;
    if (useAdminApi) {
      profilesUrl = base.replace(/\/$/, '') + '/api/admin/tenants/' + encodeURIComponent(tenantId) + '/inbound/profiles';
    } else {
      profilesUrl = base.replace(/\/$/, '') + '/api/tenant/inbound/profiles';
    }
    try {
      var res = await fetch(profilesUrl, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
      if (!res.ok) {
        populateProfiles([{ profile_id: 'default', display_name: 'Default' }], 'default');
        return;
      }
      var json = await res.json();
      var list = (json && json.profiles) ? json.profiles : [];
      populateProfiles(list, initialProfileId);
    } catch (err) {
      populateProfiles([{ profile_id: 'default', display_name: 'Default' }], 'default');
    }
  }

  async function loadInstructions() {
    if (!tenantId) {
      setStatus('Tenant ID is required to load instructions.', 'error');
      saveButton.disabled = true;
      return;
    }
    setStatus('Loading...', 'info');
    const pid = getProfileId();

    try {
      var instructions = '';
      var profilesUrl = base.replace(/\/$/, '') + '/api/tenant/inbound/profiles';
      var profilesRes = await fetch(profilesUrl, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
      if (profilesRes.ok) {
        var profilesData = await profilesRes.json();
        var list = (profilesData && profilesData.profiles) ? profilesData.profiles : [];
        var profile = list.find(function (p) { return (p.profile_id || p) === pid; });
        if (profile && typeof profile.script === 'string') {
          instructions = profile.script;
        }
      }
      if (!instructions) {
        var url = base.replace(/\/$/, '') + '/api/inbound/instructions/' + encodeURIComponent(tenantId) + '?profile_id=' + encodeURIComponent(pid);
        var response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
        if (response.ok) {
          var data = await response.json();
          if (data && typeof data.instructions === 'string') instructions = data.instructions;
          else if (typeof data === 'string') instructions = data;
        }
      }
      textarea.value = instructions;
      setStatus('Loaded.', 'success');
    } catch (err) {
      setStatus(
        'Error loading instructions: ' + (err && err.message ? err.message : 'Network error'),
        'error'
      );
    }
  }

  async function saveInstructions() {
    if (!tenantId) {
      setStatus('Tenant ID is required to save instructions.', 'error');
      return;
    }
    setSaving(true);
    setStatus('', undefined);

    const pid = getProfileId();
    const url =
      base.replace(/\/$/, '') +
      '/api/inbound/instructions/' +
      encodeURIComponent(tenantId) +
      '?profile_id=' +
      encodeURIComponent(pid);
    const payload = {
      instructions: textarea.value != null ? String(textarea.value) : ''
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let bodyText = '';
        try {
          bodyText = await response.text();
        } catch (_e) {}
        var errMsg = 'Error saving instructions: ' + response.status + (bodyText ? ' - ' + bodyText : '');
        console.error('[Inbound Instructions] Save failed:', response.status, bodyText || '(no body)');
        setStatus(errMsg, 'error');
      } else {
        var scriptValue = textarea.value != null ? String(textarea.value) : '';
        var profilePutUrl = base.replace(/\/$/, '') + '/api/tenant/inbound/profiles/' + encodeURIComponent(pid);
        try {
          var profileRes = await fetch(profilePutUrl, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ script: scriptValue })
          });
          if (!profileRes.ok) {
            console.warn('[Inbound Instructions] Profile script save (DB) failed:', profileRes.status);
          }
        } catch (profileErr) {
          console.warn('[Inbound Instructions] Profile script save (DB) error:', profileErr && profileErr.message);
        }
        setStatus('Saved.', 'success');
      }
    } catch (err) {
      var errMsg = err && err.message ? err.message : 'Network error';
      console.error('[Inbound Instructions] Save failed:', errMsg);
      setStatus('Error saving instructions: ' + errMsg, 'error');
    } finally {
      setSaving(false);
    }
  }

  profileSelect.addEventListener('change', function () {
    loadInstructions();
  });

  saveButton.addEventListener('click', function () {
    if (!saveButton.disabled) saveInstructions();
  });

  (async function init() {
    await loadProfiles();
    loadInstructions();
  })();

  return root;
}

if (typeof window !== 'undefined') window.mountInboundInstructionsUI = mountInboundInstructionsUI;
